import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Info,
  Server,
  Package,
  User,
  FileText,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  X,
  Plus,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Cpu,
  HardDrive,
  Globe,
  Layers,
  Eye,
  Mail
} from 'lucide-react';
import api from '../api/axiosConfig';
import Footer from '../components/Footer';

interface AvailableInstance {
  uuid: string;
  instance: string;
  description: string;
  cpu: number;
  ram: number;
  disk: number;
  external_ip: string;
  status: string;
  allocated: boolean;
}

interface AvailableModule {
  disk: number;
  module: string;
  description: string;
  cpu: number;
  _id: { $oid: string };
  uuid: string;
  version: string;
  long_name: string;
  date_released: string;
  status: string;
  ram: number;
  module_metadata: {
    reviewed_by: string;
    image_path?: string;
    icon_path?: string;
    name: string;
    module_owner: string;
    description: string;
    categories: string;
    created_by: string;
    version: string;
    fact_sheet_path?: string;
    contact_email?: string;
    status: string;
  };
}

const steps = [
  { label: 'Overview', icon: Info },
  { label: 'Select Instance', icon: Server },
  { label: 'Select Module', icon: Package },
  { label: 'Module Details', icon: Eye },
  { label: 'Admin Credentials', icon: User },
  { label: 'License Agreement', icon: FileText },
  { label: 'Summary & Create', icon: CheckCircle },
];

// Google Analytics
const GA_MEASUREMENT_ID = 'G-56R16ZWNK6';
function gtag(...args: any[]) {
  (window as any).dataLayer.push(args);
}
function injectGtag() {
  if (!(window as any).gtagScriptInjected) {
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    document.head.appendChild(script);
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).gtag = gtag;
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID);
    (window as any).gtagScriptInjected = true;
  }
}

const CreateInstance: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [available, setAvailable] = useState<AvailableInstance[]>([]);
  const [selected, setSelected] = useState<AvailableInstance | null>(null);
  const [availableModules, setAvailableModules] = useState<AvailableModule[]>([]);
  const [selectedModules, setSelectedModules] = useState<AvailableModule[]>([]);
  const [adminUser, setAdminUser] = useState('');
  const [adminPass, setAdminPass] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminPassConfirm, setAdminPassConfirm] = useState('');
  const [licenseAccepted, setLicenseAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  // Pagination for instances
  const [instancePage, setInstancePage] = useState(0);
  const instancesPerPage = 5;

  // Pagination for modules
  const [modulePage, setModulePage] = useState(0);
  const modulesPerPage = 5;

  // Expanded descriptions tracking
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());

  useEffect(() => {
    injectGtag();
    if (activeStep === 0 && available.length === 0) {
      fetchAvailable();
    }
    if (activeStep === 1 && availableModules.length === 0) {
      fetchModules();
    }
  }, [activeStep, available.length, availableModules.length]);

  const fetchAvailable = async () => {
    setLoading(true);
    setError('');
    try {
      const jwt = localStorage.getItem('jwt');
      const response = await api.get('/api/listInstances', {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      let instances = [];
      if (Array.isArray(response.data)) {
        instances = response.data;
      } else if (response.data && Array.isArray(response.data.instances)) {
        instances = response.data.instances;
      }
      setAvailable(instances);
    } catch {
      setError('Failed to fetch available instances.');
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    setLoading(true);
    setError('');
    try {
      const jwt = localStorage.getItem('jwt');
      const response = await api.get('/api/listModules', {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (Array.isArray(response.data)) {
        const moduleMap = new Map();
        response.data.forEach((module: AvailableModule) => {
          const existingModule = moduleMap.get(module.module);
          if (!existingModule || module.version > existingModule.version) {
            moduleMap.set(module.module, module);
          }
        });
        setAvailableModules(Array.from(moduleMap.values()));
      } else {
        setError('Invalid data format received for modules.');
        setAvailableModules([]);
      }
    } catch {
      setError('Failed to fetch available modules.');
      setAvailableModules([]);
    } finally {
      setLoading(false);
    }
  };

  const validateAdmin = () => {
    if (!adminName.trim()) return 'Name is required.';
    if (!adminUser.trim()) return 'Admin username is required.';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(adminUser.trim())) return 'Admin username must be a valid email address.';
    const forbiddenUsers = ['admin@ecosystem.ai', 'user@ecosystem.ai'];
    if (forbiddenUsers.includes(adminUser.trim().toLowerCase())) {
      return 'This admin username is not allowed. Please choose another email.';
    }
    if (!adminPass.trim()) return 'Admin password is required.';
    if (adminPass.length < 6) return 'Password should be at least 6 characters.';
    if (adminPass !== adminPassConfirm) return 'Passwords do not match.';
    return '';
  };

  const handleNext = () => {
    if (activeStep === 1 && !selected) {
      setError('Please select an instance.');
      return;
    }
    if (activeStep === 4) {
      const adminError = validateAdmin();
      if (adminError) {
        setError(adminError);
        return;
      }
    }
    if (activeStep === 5 && !licenseAccepted) {
      setError('You must accept the license agreement.');
      return;
    }
    setError('');
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((s) => s - 1);
  };

  const handleCancel = () => {
    navigate('/');
  };

  const handleCreate = async () => {
    if (!selected) return;
    setCreating(true);
    setError('');
    const jwt = localStorage.getItem('jwt');
    api
      .post(
        '/api/createInstance',
        {
          instance: selected,
          modules: selectedModules,
          signedUser: {
            email: adminUser,
            password: adminPass,
            name: adminName,
          },
        },
        {
          headers: { Authorization: `Bearer ${jwt}` },
        }
      )
      .catch((err) => {
        console.error('Instance creation error (async):', err);
      });
    setTimeout(() => {
      navigate('/', { state: { reload: true } });
      setCreating(false);
    }, 3000);
  };

  const toggleModuleSelection = (module: AvailableModule) => {
    const moduleId = module.module + '-' + module.version;
    const isSelected = selectedModules.some(
      (m) => m.module + '-' + m.version === moduleId
    );
    if (isSelected) {
      setSelectedModules(selectedModules.filter((m) => m.module + '-' + m.version !== moduleId));
    } else {
      setSelectedModules([...selectedModules, module]);
    }
  };

  const toggleDescriptionExpanded = (moduleId: string) => {
    setExpandedDescriptions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId);
      } else {
        newSet.add(moduleId);
      }
      return newSet;
    });
  };

  // Pagination calculations
  const totalInstancePages = Math.ceil(available.length / instancesPerPage);
  const paginatedInstances = available.slice(
    instancePage * instancesPerPage,
    (instancePage + 1) * instancesPerPage
  );

  const totalModulePages = Math.ceil(availableModules.length / modulesPerPage);
  const paginatedModules = availableModules.slice(
    modulePage * modulesPerPage,
    (modulePage + 1) * modulesPerPage
  );

  return (
    <div className="wizard-page">
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-header-brand">
          <img src="/icon-60.png" alt="ecosystem.Ai" className="brand-logo-img" />
          <div className="brand-text">
            <h1 className="brand-title">Create Instance</h1>
            <p className="brand-tagline">Set up your prediction environment</p>
          </div>
        </div>
        <div className="dashboard-header-actions">
          <button className="secondary-button" onClick={handleCancel}>
            <X size={16} />
            Cancel
          </button>
        </div>
      </header>

      <div className="wizard-container">
        <div className="wizard-card">
          {/* Stepper */}
          <div className="wizard-stepper">
            {steps.map((step, idx) => {
              const StepIcon = step.icon;
              return (
                <React.Fragment key={step.label}>
                  <div
                    className={`wizard-step ${activeStep === idx ? 'active' : ''} ${
                      activeStep > idx ? 'completed' : ''
                    }`}
                  >
                    <div className="wizard-step-icon">
                      {activeStep > idx ? <CheckCircle size={16} /> : <StepIcon size={16} />}
                    </div>
                    <span className="wizard-step-label">{step.label}</span>
                  </div>
                  {idx < steps.length - 1 && (
                    <div
                      className={`wizard-step-connector ${activeStep > idx ? 'completed' : ''}`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Error Alert */}
          {error && (
            <div className="alert alert-error" style={{ margin: '0 24px' }}>
              {error}
            </div>
          )}

          {/* Step Content */}
          <div className="wizard-content">
            {/* Step 0: Overview */}
            {activeStep === 0 && (
              <div>
                <h2 className="wizard-section-title">What you get with this install</h2>
                <p className="wizard-section-description">
                  Select from pre-built modules to fast-track your prediction projects
                </p>
                <div className="overview-grid">
                  <div className="overview-card">
                    <div className="overview-card-icon">
                      <Package size={20} />
                    </div>
                    <h3 className="overview-card-title">Pre-built Modules</h3>
                    <p className="overview-card-description">
                      Choose from a library of proven prediction use-cases to install and run on
                      your instance.
                    </p>
                  </div>
                  <div className="overview-card">
                    <div className="overview-card-icon">
                      <Server size={20} />
                    </div>
                    <h3 className="overview-card-title">Prediction Server</h3>
                    <p className="overview-card-description">
                      Manage data engineering, data science, and ML functions through Python,
                      Workbench, or APIs.
                    </p>
                  </div>
                  <div className="overview-card">
                    <div className="overview-card-icon">
                      <Layers size={20} />
                    </div>
                    <h3 className="overview-card-title">Workbench</h3>
                    <p className="overview-card-description">
                      No-code front-end for end-to-end prediction project lifecycle activities.
                    </p>
                  </div>
                  <div className="overview-card">
                    <div className="overview-card-icon">
                      <FileText size={20} />
                    </div>
                    <h3 className="overview-card-title">Notebooks</h3>
                    <p className="overview-card-description">
                      Default Python libraries and Jupyter Notebooks for customizations.
                    </p>
                  </div>
                  <div className="overview-card">
                    <div className="overview-card-icon">
                      <Globe size={20} />
                    </div>
                    <h3 className="overview-card-title">Client Pulse Responder (x8)</h3>
                    <p className="overview-card-description">
                      Full runtime inference and scoring for real-time activities with logging.
                    </p>
                  </div>
                  <div className="overview-card">
                    <div className="overview-card-icon">
                      <Cpu size={20} />
                    </div>
                    <h3 className="overview-card-title">Grafana</h3>
                    <p className="overview-card-description">
                      Dashboard to track use-case performance in Client Pulse Responder Runtime.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Select Instance */}
            {activeStep === 1 && (
              <div>
                <h2 className="wizard-section-title">Select an Instance</h2>
                <p className="wizard-section-description">
                  Choose an available instance to deploy your prediction environment
                </p>

                {loading ? (
                  <div className="loading-state">
                    <RefreshCw size={32} className="spinning" />
                    <span>Loading available instances...</span>
                  </div>
                ) : (
                  <>
                    {selected && (
                      <div className="alert alert-info" style={{ marginBottom: '16px' }}>
                        Selected Instance: <strong>{selected.instance}</strong>
                      </div>
                    )}
                    <div className="data-table-container" style={{ margin: 0 }}>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Instance</th>
                            <th>Description</th>
                            <th>
                              <Cpu size={14} /> CPU
                            </th>
                            <th>RAM (GB)</th>
                            <th>
                              <HardDrive size={14} /> Disk (GB)
                            </th>
                            <th>External IP</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedInstances.map((inst) => (
                            <tr
                              key={inst.uuid || inst.instance}
                              onClick={() => inst.status === 'available' && setSelected(inst)}
                              style={{
                                cursor: inst.status === 'available' ? 'pointer' : 'not-allowed',
                                opacity: inst.status === 'available' ? 1 : 0.5,
                                background:
                                  selected?.instance === inst.instance
                                    ? 'var(--primary-faded)'
                                    : undefined,
                              }}
                            >
                              <td>
                                <strong>{inst.instance}</strong>
                              </td>
                              <td>{inst.description}</td>
                              <td>{inst.cpu}</td>
                              <td>{inst.ram}</td>
                              <td>{inst.disk}</td>
                              <td>{inst.external_ip}</td>
                              <td>
                                <span
                                  className={`status-badge ${
                                    inst.status === 'available' ? 'running' : 'pending'
                                  }`}
                                >
                                  {inst.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {totalInstancePages > 1 && (
                      <div className="pagination">
                        <button
                          className="pagination-btn"
                          onClick={() => setInstancePage(Math.max(0, instancePage - 1))}
                          disabled={instancePage === 0}
                        >
                          <ChevronLeft size={16} />
                          Previous
                        </button>
                        <div className="pagination-info">
                          Page {instancePage + 1} of {totalInstancePages}
                        </div>
                        <button
                          className="pagination-btn"
                          onClick={() =>
                            setInstancePage(Math.min(totalInstancePages - 1, instancePage + 1))
                          }
                          disabled={instancePage >= totalInstancePages - 1}
                        >
                          Next
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Step 2: Select Modules */}
            {activeStep === 2 && (
              <div>
                <h2 className="wizard-section-title">Select Modules</h2>
                <p className="wizard-section-description">
                  Choose optional modules to install on your instance (you can skip this step)
                </p>

                {loading ? (
                  <div className="loading-state">
                    <RefreshCw size={32} className="spinning" />
                    <span>Loading available modules...</span>
                  </div>
                ) : (
                  <>
                    {selectedModules.length > 0 && (
                      <div className="alert alert-info" style={{ marginBottom: '16px' }}>
                        Selected Modules:{' '}
                        <strong>
                          {selectedModules.map((m) => `${m.long_name} (v${m.version})`).join(', ')}
                        </strong>
                      </div>
                    )}
                    <div className="data-table-container" style={{ margin: 0 }}>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th style={{ width: '40px' }}></th>
                            <th>Module Name</th>
                            <th>Description</th>
                            <th>Version</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedModules.map((mod) => {
                            const moduleId = mod.module + '-' + mod.version;
                            const isSelected = selectedModules.some(
                              (m) => m.module + '-' + m.version === moduleId
                            );
                            return (
                              <tr
                                key={moduleId}
                                onClick={() => toggleModuleSelection(mod)}
                                style={{
                                  cursor: 'pointer',
                                  background: isSelected ? 'var(--primary-faded)' : undefined,
                                }}
                              >
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleModuleSelection(mod)}
                                    style={{ accentColor: 'var(--primary)' }}
                                  />
                                </td>
                                <td>
                                  <strong>{mod.long_name}</strong>
                                </td>
                                <td>{mod.description}</td>
                                <td>{mod.version}</td>
                                <td>
                                  <span className="status-badge running">{mod.status}</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {totalModulePages > 1 && (
                      <div className="pagination">
                        <button
                          className="pagination-btn"
                          onClick={() => setModulePage(Math.max(0, modulePage - 1))}
                          disabled={modulePage === 0}
                        >
                          <ChevronLeft size={16} />
                          Previous
                        </button>
                        <div className="pagination-info">
                          Page {modulePage + 1} of {totalModulePages}
                        </div>
                        <button
                          className="pagination-btn"
                          onClick={() =>
                            setModulePage(Math.min(totalModulePages - 1, modulePage + 1))
                          }
                          disabled={modulePage >= totalModulePages - 1}
                        >
                          Next
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Step 3: Module Details */}
            {activeStep === 3 && (
              <div>
                <h2 className="wizard-section-title">Module Details</h2>
                <p className="wizard-section-description">
                  Review the details of the modules you have selected
                </p>

                {selectedModules.length === 0 ? (
                  <div className="alert alert-info">
                    <p>No modules selected. You can continue without selecting any modules, or go back to select modules.</p>
                  </div>
                ) : (
                  <div className="module-details-grid">
                    {selectedModules.map((mod) => (
                      <div key={mod.uuid || mod.module + '-' + mod.version} className="module-detail-card">
                        <div className="module-detail-header">
                          <div className="module-detail-icon">
                            <Package size={24} />
                          </div>
                          <div className="module-detail-header-text">
                            <h3 className="module-detail-name">
                              {mod.module_metadata?.name || mod.long_name}
                            </h3>
                            <span className="module-detail-version">v{mod.version}</span>
                          </div>
                        </div>
                        <div className="module-detail-body">
                          <div className="module-detail-field">
                            <div className="module-detail-field-label">
                              <User size={14} />
                              <span>Module Owner</span>
                            </div>
                            <div className="module-detail-field-value">
                              {mod.module_metadata?.module_owner || 'Not specified'}
                            </div>
                          </div>
                          <div className="module-detail-field">
                            <div className="module-detail-field-label">
                              <Info size={14} />
                              <span>Description</span>
                            </div>
                            <div 
                              className={`module-detail-field-value module-detail-description ${
                                expandedDescriptions.has(mod.module + '-' + mod.version) ? 'expanded' : ''
                              }`}
                              dangerouslySetInnerHTML={{
                                __html: mod.module_metadata?.description || mod.description || 'No description available'
                              }}
                            />
                            {(mod.module_metadata?.description || mod.description) && (
                              <button
                                type="button"
                                className="description-toggle"
                                onClick={() => toggleDescriptionExpanded(mod.module + '-' + mod.version)}
                              >
                                {expandedDescriptions.has(mod.module + '-' + mod.version) ? (
                                  <>
                                    <ChevronUp size={14} />
                                    Show less
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown size={14} />
                                    Show more
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                          <div className="module-detail-field">
                            <div className="module-detail-field-label">
                              <Mail size={14} />
                              <span>Contact Email</span>
                            </div>
                            <div className="module-detail-field-value">
                              {mod.module_metadata?.contact_email ? (
                                <a 
                                  href={`mailto:${mod.module_metadata.contact_email}`} 
                                  className="module-detail-email"
                                >
                                  {mod.module_metadata.contact_email}
                                </a>
                              ) : (
                                'Not specified'
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Admin Credentials */}
            {activeStep === 4 && (
              <div>
                <h2 className="wizard-section-title">Admin Credentials</h2>
                <p className="wizard-section-description">
                  These details will be used to set up the default administrator user for your
                  instance
                </p>

                <div className="wizard-form">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      value={adminName}
                      onChange={(e) => setAdminName(e.target.value)}
                      placeholder="Enter your display name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Admin Username (Email)</label>
                    <input
                      type="email"
                      value={adminUser}
                      onChange={(e) => setAdminUser(e.target.value)}
                      placeholder="admin@yourcompany.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>Admin Password</label>
                    <input
                      type="password"
                      value={adminPass}
                      onChange={(e) => setAdminPass(e.target.value)}
                      placeholder="Minimum 6 characters"
                    />
                  </div>
                  <div className="form-group">
                    <label>Confirm Password</label>
                    <input
                      type="password"
                      value={adminPassConfirm}
                      onChange={(e) => setAdminPassConfirm(e.target.value)}
                      placeholder="Re-enter your password"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: License Agreement */}
            {activeStep === 5 && (
              <div>
                <h2 className="wizard-section-title">License Agreement</h2>
                <p className="wizard-section-description">
                  Please review and accept the license agreement to proceed
                </p>

                <div className="alert alert-warning" style={{ marginBottom: '16px' }}>
                  <strong>Non-Commercial Use Only:</strong> This free version is provided strictly
                  for non-commercial use. For commercial licensing or enterprise usage, please
                  contact{' '}
                  <a
                    href="mailto:amy@ecosystem.ai"
                    style={{ color: 'var(--primary)', textDecoration: 'underline' }}
                  >
                    ecosystem.Ai
                  </a>
                  .
                </div>

                <div className="license-container">
                  <h2>Software Usage Agreement</h2>
                  <p>
                    <strong>
                      ecosystem.Ai is a brand of Ecogenetic LLC, a Delaware, USA company.
                    </strong>
                  </p>
                  <p>
                    This Software Usage Agreement (the "Agreement") is entered into by and between
                    ecosystem.Ai ("Provider") and you (the "User").
                  </p>

                  <h3>1. Grant of Access & Usage</h3>
                  <p>
                    Provider grants the User a non-exclusive, non-transferable, limited right to
                    access and use the instance ("Instance") for personal or internal business
                    purposes only, subject to compliance with this Agreement.{' '}
                    <strong>This free version is strictly for non-commercial use.</strong>
                  </p>

                  <h3>2. Intellectual Property</h3>
                  <p>
                    All rights, title, and interest in and to the Instance, including but not
                    limited to all software, modules, documentation, and any improvements or
                    derivatives thereof, remain the exclusive property of ecosystem.Ai.
                  </p>

                  <h3>3. Restrictions</h3>
                  <p>
                    The User shall not: (a) modify, adapt, or create derivative works; (b)
                    decompile, disassemble, or reverse engineer; (c) rent, lease, lend, sell, or
                    sublicense; (d) use for illegal purposes; (e) use for commercial purposes
                    without a separate commercial license.
                  </p>

                  <h3>4. No Warranty & Limitation of Liability</h3>
                  <p>
                    THE INSTANCE IS PROVIDED "AS IS" AND "AS AVAILABLE", WITHOUT WARRANTY OF ANY
                    KIND, EXPRESS OR IMPLIED.
                  </p>

                  <h3>5. Ownership & Rights</h3>
                  <p>
                    All rights not expressly granted to the User are reserved by ecosystem.Ai.
                  </p>

                  <h3>6. Governing Law</h3>
                  <p>
                    This Agreement shall be governed by and construed in accordance with the laws
                    of the jurisdiction of ecosystem.Ai.
                  </p>
                </div>

                <label className="license-checkbox">
                  <input
                    type="checkbox"
                    checked={licenseAccepted}
                    onChange={(e) => setLicenseAccepted(e.target.checked)}
                  />
                  <span>I accept the license agreement</span>
                </label>
              </div>
            )}

            {/* Step 6: Summary */}
            {activeStep === 6 && (
              <div>
                <div className="summary-section">
                  <div className="summary-header">
                    <CheckCircle size={24} className="summary-header-icon" />
                    <h2 className="summary-header-title">Summary</h2>
                  </div>

                  <div className="summary-content">
                    <div className="summary-alert">
                      <p>
                        <strong>Setup in progress:</strong> Your instance will be provisioned and
                        configured. This may take a few minutes.
                        <br />
                        Once ready, you can access it from the <strong>Instances</strong> table on
                        the home page.
                        <br />
                        Use the <strong>Admin Username</strong> and <strong>Password</strong> you
                        provided to log in.
                      </p>
                    </div>

                    <div className="summary-group">
                      <h4 className="summary-group-title">Selected Instance</h4>
                      <div className="summary-item">
                        <span className="summary-item-label">Instance Name</span>
                        <span className="summary-item-value">{selected?.instance}</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-item-label">Description</span>
                        <span className="summary-item-value">{selected?.description}</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-item-label">Resources</span>
                        <span className="summary-item-value">
                          CPU: {selected?.cpu} | RAM: {selected?.ram} GB | Disk: {selected?.disk} GB
                        </span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-item-label">External IP</span>
                        <span className="summary-item-value">{selected?.external_ip}</span>
                      </div>
                    </div>

                    <div className="summary-group">
                      <h4 className="summary-group-title">Admin User</h4>
                      <div className="summary-item">
                        <span className="summary-item-label">Name</span>
                        <span className="summary-item-value">{adminName}</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-item-label">Username</span>
                        <span className="summary-item-value">{adminUser}</span>
                      </div>
                    </div>

                    <div className="summary-group">
                      <h4 className="summary-group-title">Selected Modules</h4>
                      {selectedModules.length === 0 ? (
                        <p style={{ color: 'var(--text-muted)', paddingLeft: '8px' }}>
                          No modules selected.
                        </p>
                      ) : (
                        selectedModules.map((module) => (
                          <div key={module.uuid} className="summary-item">
                            <span className="summary-item-label">{module.long_name}</span>
                            <span className="summary-item-value">v{module.version}</span>
                          </div>
                        ))
                      )}
                    </div>

                    <button
                      className="primary-button summary-create-btn"
                      onClick={handleCreate}
                      disabled={creating}
                    >
                      {creating ? (
                        <>
                          <RefreshCw size={18} className="spinning" />
                          Creating Instance...
                        </>
                      ) : (
                        <>
                          <Plus size={18} />
                          Create Instance
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="wizard-navigation">
            <button
              className="secondary-button"
              onClick={handleBack}
              disabled={activeStep === 0 || creating}
            >
              <ArrowLeft size={16} />
              Back
            </button>
            <button className="secondary-button" onClick={handleCancel} disabled={creating}>
              Cancel
            </button>
            {activeStep < steps.length - 1 && (
              <button className="primary-button" onClick={handleNext} disabled={creating}>
                Next
                <ArrowRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CreateInstance;
